import { Button, Flex, Text } from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import { TextInput, Select } from 'react-hook-form-mantine';
import { CreatableSelect } from '@dealstack/frontend/shared/components/forms/CreatableSelect/CreatableSelect';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationMipCreate } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { CURRENCY_OPTIONS } from '@dealstack/shared/lib/format/currency/currency';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import {
	GENERAL_DEAL_INPUTS_FORM,
	IMipCreateGeneralDealInputsValues,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/GeneralDealInputs/useGeneralDealInputsController';
import { useMipEntitySelectController } from '@dealstack/frontend/app/pages/Project/MIP/MIPCreate/GeneralDealInputs/useMipEntitySelectController';
import type { QuickCreateProgrammeValues } from './CreateProgrammeDrawer.types';

interface Props {
	dealId: string;
	isCreating: boolean;
	onClose: () => void;
	onCreate: (values: QuickCreateProgrammeValues) => void;
}

export const CreateProgrammeForm = ({ dealId, isCreating, onClose, onCreate }: Props) => {
	const tMipCreate = useTranslationMipCreate();

	const form = useForm<IMipCreateGeneralDealInputsValues>({
		defaultValues: {
			[GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME]: '',
			[GENERAL_DEAL_INPUTS_FORM.ENTITY_ID]: null,
			[GENERAL_DEAL_INPUTS_FORM.INVESTOR_INSTRUCTIONS]: '',
			[GENERAL_DEAL_INPUTS_FORM.MIP_CURRENCY]: CurrencyType.EUR,
		},
	});

	const { entityOptions, onCreateNewEntity, selectedEntity } = useMipEntitySelectController(
		dealId,
		form
	);

	const programmeName = form.watch(GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME);

	const onSubmit = (values: IMipCreateGeneralDealInputsValues) => {
		onCreate({
			programmeName: values[GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME],
			entityLabel: selectedEntity?.label ?? null,
			isNewEntity: !!selectedEntity?.isNew,
			entityId: selectedEntity?.isNew ? null : selectedEntity?.value ?? null,
			currency: values[GENERAL_DEAL_INPUTS_FORM.MIP_CURRENCY],
		});
	};

	return (
		<form onSubmit={form.handleSubmit(onSubmit)}>
			<Flex direction="column" gap="1rem">
				<Text size="B6Medium" c="gray.9">
					{tMipCreate('quick_create.description')}
				</Text>

				<TextInput
					name={GENERAL_DEAL_INPUTS_FORM.PROGRAMME_NAME}
					control={form.control}
					size="md"
					label={tMipCreate('general_deal_inputs.form.programme_name_label')}
					rules={{ required: true }}
				/>

				<Controller
					name={GENERAL_DEAL_INPUTS_FORM.ENTITY_ID}
					control={form.control}
					render={({ field }) => (
						<CreatableSelect
							value={field.value ?? null}
							onChange={field.onChange}
							label={tMipCreate('general_deal_inputs.form.entity_label')}
							size="md"
							shouldCreate={(query, data) =>
								!!query && !data.find((entity) => entity.label === query)
							}
							onCreate={onCreateNewEntity}
							getCreateLabel={(query) => (
								<Flex align="center" gap="0.5rem">
									<Icon variant="add" />
									<Text>
										{tMipCreate('general_deal_inputs.form.create_new_entity')}
									</Text>
									<Text>"{query}"</Text>
								</Flex>
							)}
							data={entityOptions}
						/>
					)}
				/>

				<Select
					name={GENERAL_DEAL_INPUTS_FORM.MIP_CURRENCY}
					control={form.control}
					size="md"
					label={tMipCreate('general_deal_inputs.form.currency_label')}
					searchable
					data={CURRENCY_OPTIONS}
					rightSection={<Icon variant="caret-down" size="lg" />}
					rightSectionProps={{ style: { pointerEvents: 'none' } }}
				/>

				<Flex justify="flex-end" gap="0.5rem" mt="1rem">
					<Button variant="default" onClick={onClose} disabled={isCreating}>
						{tMipCreate('quick_create.cancel')}
					</Button>
					<Button
						type="submit"
						loading={isCreating}
						disabled={isCreating || !programmeName?.trim()}
					>
						{tMipCreate('quick_create.submit')}
					</Button>
				</Flex>
			</Flex>
		</form>
	);
};
